/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import Head from 'next/head';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useLocale } from '@/hooks/useLocale';
import { generateShelfSchema } from '../../utils/generateShelfSchema';
import type { SeoMetadataResponseModel } from '@/api/types/seo-data-types';
import type { ShelfPart } from '@/types/legacy';
import { metaTagKey } from '@/constants/seoMetaTags';

export type ShelfSeoMetaDataProps = {
  seoMetaData: SeoMetadataResponseModel;
  parts: ShelfPart[];
  shelfCatLabel?: string;
};

const isEmptySeoMetadata = (metadata: SeoMetadataResponseModel): boolean => {
  return Object.values(metadata).every((value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === null || value === '' || value === true;
  });
};

export const ShelfSeoMetaData = ({ seoMetaData, parts, shelfCatLabel }: ShelfSeoMetaDataProps) => {
  const locale = useLocale();
  const router = useLegacyRouter();

  const schema = !parts[0]
    ? ''
    : JSON.stringify(generateShelfSchema({ parts, asPath: router.asPath, locale, shelfCatLabel }));

  if (isEmptySeoMetadata(seoMetaData) && !parts[0]) {
    return null;
  }

  return (
    <>
      <Head>
        <title key={metaTagKey('title')}>{seoMetaData.pageTitle}</title>

        {seoMetaData.pageTitle ? (
          <meta key="metaTitleAlt" name="title" content={seoMetaData.pageTitle} />
        ) : null}
        {seoMetaData.indexEnabledFlag ? (
          <>
            {seoMetaData.pageContentDescription && (
              <meta
                key={metaTagKey('description')}
                name="description"
                content={seoMetaData.pageContentDescription}
              />
            )}
            {seoMetaData.pageTitle && (
              <meta key={metaTagKey('og:title')} name="og:title" content={seoMetaData.pageTitle} />
            )}
            {seoMetaData.currentPageUrl && (
              <meta
                key={metaTagKey('og:url')}
                property="og:url"
                content={seoMetaData.currentPageUrl}
              />
            )}
            {parts[0]?.productImageUrl && (
              <meta
                key={metaTagKey('og:image')}
                property="og:image"
                content={parts[0]?.productImageUrl}
              />
            )}
            {seoMetaData.siteName && (
              <meta
                key={metaTagKey('og:site_name')}
                property="og:site_name"
                content={seoMetaData.siteName}
              />
            )}
            <meta key={metaTagKey('og:type')} property="og:type" content="shelf" />
          </>
        ) : null}

        {seoMetaData.robotsContent ? (
          <meta key={metaTagKey('robots')} name="robots" content={seoMetaData.robotsContent} />
        ) : null}

        {seoMetaData.canonicalUrl && (
          <link
            key={metaTagKey('canonical')}
            rel="canonical"
            href={`${seoMetaData.canonicalUrl}`}
          />
        )}

        {seoMetaData.alternateUrls?.map((alternateUrl) => {
          return (
            <link
              key={`${metaTagKey('alternateUrl')}_${alternateUrl.locale}`}
              rel="alternate"
              hrefLang={alternateUrl.locale}
              href={alternateUrl.url}
            />
          );
        })}

        {schema ? (
          <script
            key={metaTagKey('shelfSchema')}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: schema,
            }}
          />
        ) : null}
      </Head>
    </>
  );
};
